import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.ts';
import { appsApi } from '../features/apps/api.ts';
import { Icon } from './Icon.tsx';

type SideItem = {
  to: string;
  label: string;
  icon: string;
  matchPrefix?: string;
};

function navFor(role: 'SYS_ADMIN' | 'ORG_ADMIN' | 'USER'): SideItem[] {
  if (role === 'SYS_ADMIN') {
    return [
      { to: '/admin/orgs', label: 'Organizations', icon: 'apartment', matchPrefix: '/admin/orgs' },
      { to: '/admin/subscriptions', label: 'Subscriptions', icon: 'credit_card', matchPrefix: '/admin/subscriptions' },
      { to: '/profile', label: 'Profile', icon: 'person' },
    ];
  }
  return [
    { to: '/dashboard', label: 'Home', icon: 'home', matchPrefix: '/dashboard' },
    ...(role === 'ORG_ADMIN' ? [{ to: '/team', label: 'Team', icon: 'group', matchPrefix: '/team' }] : []),
    { to: '/profile', label: 'Profile', icon: 'person' },
  ];
}

// Per-app glyph for the drawer's app section. Mirrors each app's own mark, so
// it has to move when a mark does: WapPro is a tag (not a cart) since its
// 2026-09-03 mark, and WapStat is a house (not a bar chart) since 2026-09-04.
const APP_ICON: Record<string, string> = {
  wapmeet: 'calendar_month',
  wapsign: 'draw',
  wapstat: 'home',
  wappro: 'sell',
  wapdocs: 'description',
};

// Google-Drive-style navigation drawer (desktop): full-width destination rows
// with a tonal pill on the active row, then the signed-in user's actual apps.
//
// The app section is the point of the drawer: Office's whole job is launching
// the suite, so every app the user has is one click away from any page. It is
// driven by the same ['apps','status'] query Home uses — no extra endpoint, and
// react-query dedupes the fetch. (This replaced a hardcoded two-item list that
// always named WapSign and WapMeet regardless of the user's actual access.)
export function Sidebar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isSysAdmin = user?.role === 'SYS_ADMIN';
  const { data: apps } = useQuery({
    queryKey: ['apps', 'status'],
    queryFn: () => appsApi.status(),
    // Sys admins administer orgs rather than owning app accounts, so the
    // section is meaningless for them and the request is skipped.
    enabled: !!user && !isSysAdmin,
  });

  if (!user) return null;
  const items = navFor(user.role);

  // Deliberately the SAME launchability rule as the Home app cards
  // (features/dashboard/AppCard.tsx: `hasAccount || statusUnavailable`), so the
  // drawer and Home can never disagree about what the user can open. Note it
  // ignores `inDevelopment` — WapStat and WapPro still carry that flag from the
  // backend despite both being live, and Home has always launched them.
  const myApps = (apps ?? []).filter((a) => !a.tile && (a.hasAccount || a.statusUnavailable));

  const isActive = (item: SideItem): boolean =>
    item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;

  const rowBase = 'flex items-center gap-4 rounded-full px-4 py-2.5 text-[0.95rem] transition-colors';
  const idleRow = 'text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]';

  return (
    <aside
      aria-label="Primary"
      className="hidden md:flex fixed left-0 top-16 bottom-0 z-30 w-64 flex-col gap-1 px-3 py-4 bg-[hsl(var(--chrome))] overflow-y-auto"
    >
      <nav className="flex flex-col gap-0.5">
        {items.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={[
                rowBase,
                active
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] font-medium'
                  : idleRow,
              ].join(' ')}
            >
              <Icon name={item.icon} size={22} fill={active} className={active ? '' : 'text-muted-foreground'} />
              <span className="flex-1 truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {myApps.length > 0 && (
        <>
          <hr className="my-3 border-0 border-t border-[hsl(var(--border))]/50" />
          <h2 className="px-4 pb-1 text-xs font-medium text-muted-foreground">Your apps</h2>
          <nav className="flex flex-col gap-0.5" aria-label="Your apps">
            {myApps.map((app) => (
              <a
                key={app.key}
                href={app.url}
                className={`${rowBase} ${idleRow}`}
              >
                <Icon
                  name={APP_ICON[app.key] ?? 'apps'}
                  size={22}
                  className="text-muted-foreground"
                />
                <span className="flex-1 truncate">{app.name}</span>
                <Icon name="open_in_new" size={16} className="text-muted-foreground" />
              </a>
            ))}
          </nav>
        </>
      )}
    </aside>
  );
}
